import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../Button';
import { 
  Check, 
  ShieldCheck, 
  Clock, 
  Sparkles, 
  ChevronRight,
  Home,
  Building2,
  Paintbrush,
  HardHat,
  Coins,
  Gem,
  Crown,
  Zap,
  Calendar,
  Search
} from 'lucide-react';
import { submitLeadForm } from '../../services/formSubmit';
import { useTheme } from '../../context/ThemeContext';
import ReactGA from 'react-ga4';

export function LeadCaptureForm() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: '',
    budget: '',
    timeline: '',
    name: '',
    email: '',
    phone: '',
    city: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  // Auto-detect city from URL path on mount
  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    if (path.includes('hyderabad')) {
      setFormData(prev => ({ ...prev, city: 'Hyderabad' }));
    } else if (path.includes('vijayawada')) {
      setFormData(prev => ({ ...prev, city: 'Vijayawada' }));
    } else if (path.includes('bangalore')) {
      setFormData(prev => ({ ...prev, city: 'Bangalore' }));
    }
  }, []);

  const handleOptionSelect = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (step < totalSteps) {
      setTimeout(() => setStep(prev => prev + 1), 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await submitLeadForm(formData);
      ReactGA.event({ category: 'Lead', action: 'Form Submitted', label: formData.projectType });
      setIsSuccess(true);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Option lists with premium icons and description subtexts
  const step1Options = [
    { value: 'Home Interior', label: 'Home Interior', Icon: Home, desc: 'Complete design for apartments, villas & independent homes' },
    { value: 'Office / Commercial', label: 'Office / Commercial', Icon: Building2, desc: 'Productive office designs, retail outlets & cabins' },
    { value: 'Renovation', label: 'Renovation', Icon: Paintbrush, desc: 'Smart transformation of kitchens, bedrooms, or living areas' },
    { value: 'Turnkey Construction', label: 'Turnkey Construction', Icon: HardHat, desc: 'A-to-Z construction from architectural planning to final handover' }
  ];

  const step2Options = [
    { value: '₹2L – ₹5L', label: '₹2L – ₹5L', Icon: Coins, desc: 'Budget-friendly modular kitchen & wardrobe essentials' },
    { value: '₹5L – ₹10L', label: '₹5L – ₹10L', Icon: Sparkles, desc: 'Premium finishes & comprehensive standard woodwork' },
    { value: '₹10L – ₹20L', label: '₹10L – ₹20L', Icon: Gem, desc: 'Luxury materials, smart automation & custom designs' },
    { value: '₹20L+', label: '₹20L+', Icon: Crown, desc: 'High-end bespoke luxury, imported fittings & turnkey' }
  ];

  const step3Options = [
    { value: 'Immediately', label: 'Immediately', Icon: Zap, desc: 'Looking to start within the next 2-3 weeks' },
    { value: 'Within 1-2 Months', label: 'Within 1-2 Months', Icon: Calendar, desc: 'Finalizing builders / keys possession soon' },
    { value: 'Just Planning', label: 'Just Planning', Icon: Search, desc: 'Exploring ideas & getting cost estimations' }
  ];

  const stepsInfo = [
    { label: 'Project Type' },
    { label: 'Budget' },
    { label: 'Timeline' },
    { label: 'Contact' }
  ];

  return (
    <section id="consultation" aria-label="Free design consultation form" className={`py-24 md:py-32 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-dark-bg' : 'bg-slate-50'}`}>
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute inset-0 bg-[radial-gradient(ellipse_at_center,var(--tw-gradient-stops))] ${isDark ? 'from-accent/5 via-dark-bg to-dark-bg' : 'from-accent/5 via-slate-50 to-slate-50'} opacity-70`} />
        
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3], x: [0, 30, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-accent/20 blur-[120px]"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2], x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className={`absolute bottom-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full ${isDark ? 'bg-purple-900/30' : 'bg-rose-100/50'} blur-[120px]`}
        />
        
        {/* Subtle Grid */}
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[24px_24px] ${isDark ? 'opacity-20' : 'opacity-40'}`} />
      </div>
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs sm:text-sm font-bold text-accent uppercase tracking-widest mb-3"
          >
            Get Started
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className={`text-3xl sm:text-4xl lg:text-5xl font-poppins font-bold tracking-tight ${isDark ? 'text-white' : 'text-primary'}`}
          >
            Request a Free Design Consultation
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold ${
              isDark ? 'bg-accent/15 border border-accent/20 text-accent' : 'bg-accent/10 text-primary border border-accent/20'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-accent animate-pulse" />
            <span>Book today & get a Free 3D Layout Blueprint</span>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className={`relative rounded-4xl p-8 md:p-12 border backdrop-blur-xl transition-all duration-500 overflow-hidden ${isDark ? 'bg-[#1E293B]/80 border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.3)]' : 'bg-white/80 border-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)]'}`}
        >
          {/* Inner glow */}
          <div className={`absolute inset-0 opacity-50 ${isDark ? 'bg-linear-to-b from-white/5 to-transparent' : 'bg-linear-to-b from-white/50 to-transparent'} pointer-events-none`} />
          
          <div className="relative z-10">
          {!isSuccess ? (
            <>
              {/* Progress Milestones Tracker (Desktop/Tablet) */}
              <div className="hidden sm:flex justify-between items-center mb-12 relative px-4">
                {stepsInfo.map((s, idx) => {
                  const isCompleted = step > idx + 1;
                  const isActive = step === idx + 1;
                  return (
                    <div key={idx} className="flex flex-col items-center flex-1 relative z-10">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
                        isCompleted 
                          ? 'bg-accent text-white shadow-[0_0_15px_rgba(201,162,107,0.4)] border border-accent/30' 
                          : isActive 
                            ? 'bg-accent text-white ring-4 ring-accent/25 scale-110 shadow-[0_0_20px_rgba(201,162,107,0.5)] font-black' 
                            : isDark 
                              ? 'bg-slate-800 border-2 border-slate-700 text-slate-400' 
                              : 'bg-white border-2 border-gray-200 text-gray-400'
                      }`}>
                        {isCompleted ? <Check className="w-5 h-5 stroke-[2.5]" /> : idx + 1}
                      </div>
                      <span className={`text-[10px] sm:text-xs uppercase font-extrabold tracking-widest mt-3.5 transition-all duration-300 font-poppins ${
                        isActive 
                          ? 'text-accent scale-105' 
                          : isDark 
                            ? isCompleted ? 'text-accent/80' : 'text-slate-500'
                            : isCompleted ? 'text-accent/80' : 'text-gray-450'
                      }`}>
                        {s.label}
                      </span>
                    </div>
                  );
                })}
                {/* Connecting Line */}
                <div className={`absolute top-5 left-[12.5%] right-[12.5%] h-[3px] -z-10 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                  <div 
                    className="h-full bg-accent rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(201,162,107,0.6)]"
                    style={{ width: `${((step - 1) / (stepsInfo.length - 1)) * 100}%` }}
                  />
                </div>
              </div>

              {/* Progress Bar (Mobile only) */}
              <div className="sm:hidden mb-8">
                <div className={`h-2 w-full rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <motion.div 
                    className="h-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="text-right text-xs text-gray-400 mt-2 font-medium tracking-widest uppercase">
                  Step {step} of {totalSteps}
                </div>
              </div>

              <div className="min-h-[280px]">
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <h4 className={`text-lg sm:text-xl font-poppins font-bold ${isDark ? 'text-white' : 'text-primary'}`}>What type of project are you looking for?</h4>
                      <div className="grid grid-cols-1 gap-3.5">
                        {step1Options.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOptionSelect('projectType', opt.value)}
                            className={`group w-full flex items-center gap-4 px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left ${
                              formData.projectType === opt.value 
                                ? 'border-accent bg-accent/10' 
                                : isDark
                                  ? 'border-white/10 hover:border-accent/30 hover:bg-white/5 bg-white/5 text-gray-300 hover:bg-white/10'
                                  : 'border-gray-100 hover:border-accent/20 hover:bg-slate-50 bg-white text-slate-700'
                            }`}
                          >
                            <div className={`p-2.5 rounded-lg transition-colors duration-300 ${
                              formData.projectType === opt.value 
                                ? 'bg-accent/20 text-accent' 
                                : isDark
                                  ? 'bg-white/5 text-gray-400 group-hover:text-accent group-hover:bg-accent/10'
                                  : 'bg-slate-100 text-slate-500 group-hover:text-accent group-hover:bg-accent/10'
                            }`}>
                              <opt.Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className={`font-bold font-poppins text-sm sm:text-base ${formData.projectType === opt.value ? 'text-accent font-extrabold' : isDark ? 'text-white' : 'text-primary'}`}>
                                {opt.label}
                              </div>
                              <div className={`text-xs mt-0.5 leading-normal ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                {opt.desc}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                      
                      <div className={`pt-6 border-t flex flex-col items-center gap-3 ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                        <button
                          type="button"
                          onClick={() => setStep(4)}
                          className="group inline-flex items-center gap-1.5 text-xs font-bold text-accent hover:text-accent-hover transition-colors font-poppins"
                        >
                          <span>⚡ Skip questions & request quick callback</span>
                          <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <h4 className={`text-lg sm:text-xl font-poppins font-bold ${isDark ? 'text-white' : 'text-primary'}`}>What is your expected budget range?</h4>
                      <div className="grid grid-cols-1 gap-3.5">
                        {step2Options.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOptionSelect('budget', opt.value)}
                            className={`group w-full flex items-center gap-4 px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left ${
                              formData.budget === opt.value 
                                ? 'border-accent bg-accent/10' 
                                : isDark
                                  ? 'border-white/10 hover:border-accent/30 hover:bg-white/5 bg-white/5 text-gray-300 hover:bg-white/10'
                                  : 'border-gray-100 hover:border-accent/20 hover:bg-slate-50 bg-white text-slate-700'
                            }`}
                          >
                            <div className={`p-2.5 rounded-lg transition-colors duration-300 ${
                              formData.budget === opt.value 
                                ? 'bg-accent/20 text-accent' 
                                : isDark
                                  ? 'bg-white/5 text-gray-400 group-hover:text-accent group-hover:bg-accent/10'
                                  : 'bg-slate-100 text-slate-500 group-hover:text-accent group-hover:bg-accent/10'
                            }`}>
                              <opt.Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className={`font-bold font-poppins text-sm sm:text-base ${formData.budget === opt.value ? 'text-accent font-extrabold' : isDark ? 'text-white' : 'text-primary'}`}>
                                {opt.label}
                              </div>
                              <div className={`text-xs mt-0.5 leading-normal ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                {opt.desc}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className={`pt-6 border-t flex justify-between items-center ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                        <button 
                          type="button"
                          onClick={() => setStep(1)} 
                          className={`text-xs font-semibold ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-primary'}`}
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={() => setStep(4)}
                          className="group inline-flex items-center gap-1.5 text-xs font-bold text-accent hover:text-accent-hover transition-colors font-poppins"
                        >
                          <span>⚡ Skip questions & request callback</span>
                          <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <h4 className={`text-lg sm:text-xl font-poppins font-bold ${isDark ? 'text-white' : 'text-primary'}`}>When do you want to start?</h4>
                      <div className="grid grid-cols-1 gap-3.5">
                        {step3Options.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleOptionSelect('timeline', opt.value)}
                            className={`group w-full flex items-center gap-4 px-6 py-4 rounded-xl border-2 transition-all duration-300 text-left ${
                              formData.timeline === opt.value 
                                ? 'border-accent bg-accent/10' 
                                : isDark
                                  ? 'border-white/10 hover:border-accent/30 hover:bg-white/5 bg-white/5 text-gray-300 hover:bg-white/10'
                                  : 'border-gray-100 hover:border-accent/20 hover:bg-slate-50 bg-white text-slate-700'
                            }`}
                          >
                            <div className={`p-2.5 rounded-lg transition-colors duration-300 ${
                              formData.timeline === opt.value 
                                ? 'bg-accent/20 text-accent' 
                                : isDark
                                  ? 'bg-white/5 text-gray-400 group-hover:text-accent group-hover:bg-accent/10'
                                  : 'bg-slate-100 text-slate-500 group-hover:text-accent group-hover:bg-accent/10'
                            }`}>
                              <opt.Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className={`font-bold font-poppins text-sm sm:text-base ${formData.timeline === opt.value ? 'text-accent font-extrabold' : isDark ? 'text-white' : 'text-primary'}`}>
                                {opt.label}
                              </div>
                              <div className={`text-xs mt-0.5 leading-normal ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                                {opt.desc}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className={`pt-6 border-t flex justify-between items-center ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                        <button 
                          type="button"
                          onClick={() => setStep(2)} 
                          className={`text-xs font-semibold ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-primary'}`}
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={() => setStep(4)}
                          className="group inline-flex items-center gap-1.5 text-xs font-bold text-accent hover:text-accent-hover transition-colors font-poppins"
                        >
                          <span>⚡ Skip questions & request callback</span>
                          <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {step === 4 && (
                    <motion.form
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleSubmit}
                      className="space-y-6"
                    >
                      <h4 className={`text-lg sm:text-xl font-poppins font-bold ${isDark ? 'text-white' : 'text-primary'}`}>Where should we reach you?</h4>
                      
                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Full Name</label>
                          <input 
                            required
                            type="text" 
                            className={`w-full px-4 py-3.5 rounded-xl border focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400 hover:bg-white/10 hover:border-white/20' : 'bg-white border-gray-200 text-primary hover:border-gray-300'}`}
                            value={formData.name}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Phone Number</label>
                            <input 
                              required
                              type="tel" 
                              className={`w-full px-4 py-3.5 rounded-xl border focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400 hover:bg-white/10 hover:border-white/20' : 'bg-white border-gray-200 text-primary hover:border-gray-300'}`}
                              value={formData.phone}
                              onChange={e => setFormData({...formData, phone: e.target.value})}
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              Email Address <span className={`text-[10px] font-semibold tracking-wider uppercase ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(Optional)</span>
                            </label>
                            <input 
                              type="email" 
                              className={`w-full px-4 py-3.5 rounded-xl border focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400 hover:bg-white/10 hover:border-white/20' : 'bg-white border-gray-200 text-primary hover:border-gray-300'}`}
                              value={formData.email}
                              onChange={e => setFormData({...formData, email: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>City / Location</label>
                          <input 
                            required
                            type="text"
                            placeholder="e.g. Hyderabad, Vijayawada"
                            className={`w-full px-4 py-3.5 rounded-xl border focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400 hover:bg-white/10 hover:border-white/20' : 'bg-white border-gray-200 text-primary hover:border-gray-300'}`}
                            value={formData.city}
                            onChange={e => setFormData({...formData, city: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="pt-4 flex justify-between items-center">
                        <button 
                          type="button"
                          onClick={() => setStep(3)} 
                          className={`text-sm ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-primary'}`}
                        >
                          ← Back
                        </button>
                        <Button variant="primary" disabled={isSubmitting}>
                          {isSubmitting ? 'Submitting...' : 'Get Consultation'}
                        </Button>
                      </div>
                      {error && <p className="text-red-500 text-sm text-center mt-3">{error}</p>}
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isDark ? 'bg-green-900/30 ring-8 ring-green-500/10' : 'bg-green-100 ring-8 ring-green-50'}`}>
                <Check className={`w-10 h-10 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <h4 className={`text-2xl sm:text-3xl font-poppins font-bold mb-3 ${isDark ? 'text-white' : 'text-primary'}`}>Request Received!</h4>
              <p className={`mb-8 max-w-md mx-auto leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Thank you for reaching out. One of our senior design experts will review your requirements and contact you shortly.
              </p>

              <Button type="button" onClick={() => window.location.reload()} variant={isDark ? 'accent' : 'outline'}>
                Back to Home
              </Button>
            </motion.div>
          )}
          </div>

          {/* Persistent Trust Badges (Reassurance Signals) */}
          {!isSuccess && (
            <div className={`mt-8 pt-6 border-t flex flex-wrap gap-x-4 gap-y-3 justify-around items-center text-[10px] sm:text-xs font-bold uppercase tracking-wider ${
              isDark ? 'border-white/5 text-gray-500' : 'border-slate-100 text-slate-400'
            }`}>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4.5 h-4.5 text-green-500" />
                <span>100% Privacy</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4.5 h-4.5 text-accent animate-pulse" />
                <span>45-Day Handover</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-4.5 h-4.5 text-amber-500" />
                <span>Transparent Pricing</span>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
